import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { StoreContext } from "shared/context/store-context";
import { AuthContext } from "shared/context/auth-context";
import AutomationBase from "./AutomationBase";
import { SERVICE_TYPES, PLANS } from "@routebot/constants";
import RoutesAnalyzer from "components/shared/dragger/RoutesAnalyzer";
import { useRouteLimit } from "shared/hooks/useRouteLimit";
import RouteLimitBlocker from "components/shared/RouteLimitBlocker";

const CreateSchoolRoutesAutomation = ({ onClose }) => {
  const { t } = useTranslation();
  const { studentSchoolOptions, students } = useContext(StoreContext);
  const auth = useContext(AuthContext);
  const [processResults, setProcessResults] = useState(null);

  // Route limit check
  const { limitStatus, isLimitReached, isUnlimited } = useRouteLimit();
  const shouldBlock = isLimitReached && !isUnlimited && auth?.company?.plan !== PLANS.UNLIMITED;

  // Configuration for school routes automation
  const automationConfig = {
    steps: [
      t("createSchoolRoutesAutomation.step1"),
      t("createSchoolRoutesAutomation.step2"),
      t("createSchoolRoutesAutomation.step3"),
      t("createSchoolRoutesAutomation.step4"),
    ],
    schoolOptions: studentSchoolOptions,

    passengers: students,
    serviceType: SERVICE_TYPES.STUDENT,
    fileUploadConfig: {
      isFileUploadAllowed: true,
      exampleFileName: "student_list.xlsx",
      accept: ".xlsx,.xls,.csv",
    },
    texts: {
      schoolOptionsLabel: t("createSchoolRoutesAutomation.schoolOptionsLabel"),
      routeTypeLabel: t("createSchoolRoutesAutomation.routeTypeLabel"),
      maxNumberOfPassengersInARouteLabel: t("createSchoolRoutesAutomation.maxNumberOfPassengersInARouteLabel"),
      finishButtonText: t("createSchoolRoutesAutomation.finishButtonText"),
      maxNumberOfPassengersInARouteErrorText: t("createSchoolRoutesAutomation.maxNumberOfPassengersInARouteErrorText"),
      passsengerSelection: {
        title: t("createSchoolRoutesAutomation.studentSelectionTitle"),
        subtitle: t("createSchoolRoutesAutomation.studentSelectionSubtitle"),
        selectPassengerLabel: t("createSchoolRoutesAutomation.selectStudent"),
        addPassengerButtonText: t("createSchoolRoutesAutomation.addStudent"),
        completeButtonText: t("createSchoolRoutesAutomation.complete"),
        emptyStateText: t("createSchoolRoutesAutomation.emptyStateText"),
        passengerLabel: t("createSchoolRoutesAutomation.student"),
        selectedPassengersTitle: t("createSchoolRoutesAutomation.distributablePassengersTitle"),
        // Cards
        maxPassengersCardTitle: t("createSchoolRoutesAutomation.maxPassengersCardTitle"),
        routeTypeCardTitle: t("createSchoolRoutesAutomation.routeTypeCardTitle"),
        selectedPassengersCountTitle: t("createSchoolRoutesAutomation.selectedPassengersCountTitle"),
        // Sections
        notFoundHeader: t("createSchoolRoutesAutomation.notFoundHeaderBase"),
        notFoundDescription: t("createSchoolRoutesAutomation.notFoundDescription"),
        multipleHeader: t("createSchoolRoutesAutomation.multipleHeaderBase"),
        multipleDescription: t("createSchoolRoutesAutomation.multipleDescription"),
        noLocationHeader: t("createSchoolRoutesAutomation.noLocationHeaderBase"),
        noLocationDescription: t("createSchoolRoutesAutomation.noLocationDescription"),
        // Modal
        addAllModalTitle: t("createSchoolRoutesAutomation.addAllModalTitle"),
        modalCancelText: t("createSchoolRoutesAutomation.cancelButton"),
        modalAddText: t("createSchoolRoutesAutomation.addButton"),
        // Formatters (parent controls dynamic texts)
        formatAddAllButtonText: (count) => t("createSchoolRoutesAutomation.addAllButton", { count }),
        formatAddAllModalText: (count) => t("createSchoolRoutesAutomation.addAllModalText", { count }),
        // Toasts
        toastNoLocation: t("createSchoolRoutesAutomation.toastNoLocation"),
        toastAdded: t("createSchoolRoutesAutomation.toastAdded"),
        toastAlreadySelected: t("createSchoolRoutesAutomation.toastAlreadySelected"),
      },
    },

    onProcessComplete: async (data) => {
      setProcessResults(data);
      onClose();
    },
  };

  return (
    <React.Fragment>
      {shouldBlock && !processResults && (
        <RouteLimitBlocker limitStatus={limitStatus} onClose={onClose} isOverlay={true} />
      )}
      {processResults ? (
        <RoutesAnalyzer
          schoolId={processResults?.school?.toString() || ""}
          isApproved={processResults?.isApproved || false}
          routeType={processResults?.routeType || ""}
          serviceType={processResults?.serviceType || ""}
          maxNumberofPassengersInARoute={processResults?.maxNumberofPassengersInARoute || 0}
          draftServices={processResults?.draftServices || []}
          createdAt={processResults?.createdAt || ""}
        />
      ) : (
        <div className={shouldBlock ? "route-limit-blocked-form" : ""}>
          <AutomationBase {...automationConfig} />
        </div>
      )}
    </React.Fragment>
  );
};

export default CreateSchoolRoutesAutomation;
